import { useState, useRef, useEffect } from "react";
import { BottomSheet } from "./BottomSheet";
import { Check, Search } from "lucide-react";
import { Flag } from "@/utils/flag";
import { COUNTRIES } from "@/utils/countries";
import { hapticSelection } from "@/lib/haptics";

interface PhoneCountryBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
}

export const PhoneCountryBottomSheet = ({ isOpen, onClose, value, onChange }: PhoneCountryBottomSheetProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    } else {
      // Auto-focus search on open with delay for iOS animation
      const timer = setTimeout(() => inputRef.current?.focus(), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const filtered = COUNTRIES.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.label.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.dialCode.includes(searchQuery);
  });

  const handleSelect = (code: string) => {
    hapticSelection();
    onChange(code);
    onClose();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Country Code">
      <div className="space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'hsl(var(--ivory) / 0.3)' }} />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search country..."
            className="w-full h-[44px] pl-10 pr-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold)/0.3)] focus:border-[hsl(var(--gold)/0.3)]"
            style={{
              background: 'hsl(var(--ivory) / 0.04)',
              border: '1px solid hsl(var(--gold) / 0.12)',
              color: 'hsl(var(--ivory))',
            }}
          />
        </div>

        {/* Country list */}
        <div className="overflow-y-auto -mx-1 px-1 space-y-0.5" style={{ minHeight: '200px' }}>
          {filtered.length === 0 && (
            <p className="text-center text-sm py-6" style={{ color: 'hsl(var(--ivory) / 0.3)' }}>No countries found</p>
          )}
          {filtered.map((country) => (
            <button
              key={country.code}
              onClick={() => handleSelect(country.code)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
              style={{
                background: value === country.code ? 'hsl(var(--gold) / 0.1)' : 'transparent',
                border: value === country.code ? '1px solid hsl(var(--gold) / 0.25)' : '1px solid transparent',
              }}
            >
              <Flag code={country.code} size={20} className="rounded-[3px] flex-shrink-0" />
              <span className="text-sm flex-1 text-left" style={{ color: 'hsl(var(--ivory))' }}>{country.label}</span>
              <span className="text-xs font-mono flex-shrink-0" style={{ color: 'hsl(var(--gold) / 0.6)' }}>{country.dialCode}</span>
              {value === country.code && (
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(var(--gold))' }} />
              )}
            </button>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
};
